import React, { useState, useEffect } from 'react';
import {
  MapPin,
  Heart,
  Star,
  Clock,
  TrendingUp,
  Brain,
  Utensils,
  Target,
  ChefHat,
  Globe,
  Zap,
  Award
} from 'lucide-react';
import { UserPersonalizationProfile } from '../../types/users';
import { PersonalizedFoodRecommendationEngine } from '../../utils/personalizedFoodRecommendations';

interface AdvancedMealRecommendationsProps {
  profile: UserPersonalizationProfile;
  onSelectMeal: (mealName: string) => void;
}

const AdvancedMealRecommendations: React.FC<AdvancedMealRecommendationsProps> = ({
  profile,
  onSelectMeal
}) => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [selectedMealType, setSelectedMealType] = useState<'breakfast' | 'lunch' | 'dinner' | 'snack'>('breakfast');
  const [recommendations, setRecommendations] = useState<any[]>([]);
  const engine = new PersonalizedFoodRecommendationEngine();

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    // Determine meal type based on time of day
    const hour = currentTime.getHours();
    let mealType: 'breakfast' | 'lunch' | 'dinner' | 'snack' = 'breakfast';
    
    if (hour >= 6 && hour < 11) mealType = 'breakfast';
    else if (hour >= 11 && hour < 16) mealType = 'lunch';
    else if (hour >= 16 && hour < 21) mealType = 'dinner';
    else mealType = 'snack';
    
    setSelectedMealType(mealType);
    
    const timeString = `${hour}:${currentTime.getMinutes()}`;
    const recs = engine.generateRecommendations(profile, mealType, timeString);
    setRecommendations(recs);
  }, [currentTime, profile]);

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'regional': return <MapPin className="h-4 w-4 text-blue-500" />;
      case 'health': return <Heart className="h-4 w-4 text-red-500" />;
      case 'preference': return <Star className="h-4 w-4 text-yellow-500" />;
      case 'energy': return <Zap className="h-4 w-4 text-orange-500" />;
      case 'cuisine': return <Globe className="h-4 w-4 text-green-500" />;
      default: return <Utensils className="h-4 w-4 text-slate-500" />;
    }
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 90) return 'text-green-600 bg-green-50';
    if (confidence >= 80) return 'text-blue-600 bg-blue-50';
    if (confidence >= 70) return 'text-yellow-600 bg-yellow-50';
    return 'text-slate-600 bg-slate-50';
  };

  const getMealTypeIcon = (mealType: string) => {
    switch (mealType) {
      case 'breakfast': return '🌅';
      case 'lunch': return '☀️';
      case 'dinner': return '🌙';
      case 'snack': return '🍿';
      default: return '🍽️';
    }
  };

  const getAIPersonalityResponse = (mealName: string) => {
    const responses = {
      encouraging: [
        `Great choice! ${mealName} is perfect for you! 🌟`,
        `Love it! ${mealName} aligns perfectly with your goals! ✨`,
        `Excellent pick! ${mealName} will fuel your success! 💪`
      ],
      professional: [
        `${mealName} selected. This choice supports your health objectives.`,
        `Nutritional data indicates ${mealName} is optimal for your profile.`,
        `${mealName} documented. Evidence-based recommendation confirmed.`
      ],
      casual: [
        `Nice! ${mealName} sounds tasty! 😊`,
        `Cool choice! ${mealName} looks good! 👍`,
        `Sweet! ${mealName} is a solid pick! 🎉`
      ],
      motivational: [
        `BOOM! ${mealName} is PERFECT! You're CRUSHING your nutrition! 🔥`,
        `YES! ${mealName} is EXACTLY what champions eat! ⚡`,
        `AMAZING choice! ${mealName} will POWER your success! 🚀`
      ]
    };
    
    const personalityResponses = responses[profile.aiPersonality] || responses.encouraging;
    return personalityResponses[Math.floor(Math.random() * personalityResponses.length)];
  };

  return (
    <div className="space-y-6">
      {/* Header with time-based meal suggestion */}
      <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl border border-purple-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
              <Brain className="h-6 w-6 text-white" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-slate-800">AI-Powered Meal Recommendations</h3>
              <p className="text-slate-600">Personalized for {profile.location || 'your region'} • {profile.aiPersonality} style</p>
            </div>
          </div>
          <div className="text-right">
            <div className="text-2xl mb-1">{getMealTypeIcon(selectedMealType)}</div>
            <div className="text-sm text-slate-600 capitalize">{selectedMealType} Time</div>
          </div>
        </div>

        {/* Meal Type Selector */}
        <div className="flex space-x-2 mb-4">
          {(['breakfast', 'lunch', 'dinner', 'snack'] as const).map((mealType) => (
            <button
              key={mealType}
              onClick={() => setSelectedMealType(mealType)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors capitalize ${
                selectedMealType === mealType
                  ? 'bg-purple-500 text-white'
                  : 'bg-white text-slate-600 hover:bg-purple-50'
              }`}
            >
              {getMealTypeIcon(mealType)} {mealType}
            </button>
          ))}
        </div>
      </div>

      {/* Recommendations Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {recommendations.map((rec, index) => (
          <div
            key={index}
            className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-lg transition-all cursor-pointer group"
            onClick={() => {
              onSelectMeal(rec.foodName);
              // Show AI response based on personality
              const response = getAIPersonalityResponse(rec.foodName);
              // This would typically trigger a notification or toast
              console.log(response);
            }}
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center space-x-2">
                {getCategoryIcon(rec.category)}
                <h4 className="font-semibold text-slate-800 group-hover:text-purple-600 transition-colors">
                  {rec.foodName}
                </h4>
              </div>
              <div className={`px-2 py-1 rounded-full text-xs font-medium ${getConfidenceColor(rec.confidence)}`}>
                {rec.confidence}% match
              </div>
            </div>

            <p className="text-sm text-slate-600 mb-3">{rec.recommendationReason}</p>
            
            {rec.nutritionalBenefit && (
              <div className="bg-slate-50 rounded-lg p-3 mb-3">
                <p className="text-xs text-slate-700">
                  <span className="font-medium">Nutritional Benefit:</span> {rec.nutritionalBenefit}
                </p>
              </div>
            )}

            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2 text-xs text-slate-500">
                <Clock className="h-3 w-3" />
                <span>Recommended {rec.frequency}</span>
              </div>
              
              {rec.timeOfDay && (
                <div className="flex items-center space-x-1 text-xs text-blue-600">
                  <Target className="h-3 w-3" />
                  <span>Perfect timing</span>
                </div>
              )}
            </div>

            {/* Category-specific badges */}
            <div className="flex flex-wrap gap-1 mt-3">
              {rec.category === 'regional' && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-700">
                  <MapPin className="h-3 w-3 mr-1" />
                  Local favorite
                </span>
              )}
              {rec.category === 'health' && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-red-100 text-red-700">
                  <Heart className="h-3 w-3 mr-1" />
                  Health goal
                </span>
              )}
              {rec.category === 'preference' && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-yellow-100 text-yellow-700">
                  <Star className="h-3 w-3 mr-1" />
                  Your taste
                </span>
              )}
              {rec.category === 'energy' && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-orange-100 text-orange-700">
                  <Zap className="h-3 w-3 mr-1" />
                  Energy boost
                </span>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Profile Summary */}
      <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl border border-blue-200 p-6">
        <div className="flex items-center space-x-3 mb-4">
          <Award className="h-6 w-6 text-blue-500" />
          <h3 className="text-lg font-semibold text-slate-800">Your Personalization Profile</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <h4 className="font-medium text-slate-700 mb-2">Health Goals</h4>
            <div className="flex flex-wrap gap-1">
              {profile.healthGoals.map((goal, index) => (
                <span key={index} className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs">
                  {goal.replace('_', ' ')}
                </span>
              ))}
            </div>
          </div>
          
          <div>
            <h4 className="font-medium text-slate-700 mb-2">Food Preferences</h4>
            <div className="flex flex-wrap gap-1">
              {profile.foodPreferences.favoriteCategories.slice(0, 3).map((category, index) => (
                <span key={index} className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs">
                  {category}
                </span>
              ))}
            </div>
          </div>
          
          <div>
            <h4 className="font-medium text-slate-700 mb-2">AI Personality</h4>
            <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium capitalize">
              {profile.aiPersonality}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdvancedMealRecommendations; 
